import React, { useEffect, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { useResetPasswordMutation } from "@/store/rest_api";
import {
  hookFormValidatePassword,
  PASSWORD_MIN_LENGTH,
  PASSWORD_REQUIREMENTS_HINT,
} from "@/util/validation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ResetPasswordForm() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const [resetPassword, resetPasswordResult] = useResetPasswordMutation();
  const [serverError, setServerError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();

  const newPassword = watch("newPassword");

  const onSubmit = async (data: FieldValues) => {
    if (!token) {
      setServerError("Missing reset token");
      return;
    }
    setServerError(null);
    try {
      await resetPassword({ token, newPassword: data.newPassword as string }).unwrap();
      toast.success("Password updated. Please sign in.");
      navigate("/login");
    } catch (err) {
      const message =
        (err as { data?: { message?: string } })?.data?.message ?? "Reset link is invalid or expired";
      setServerError(message);
    }
  };

  useEffect(() => {
    if (!token) {
      setServerError("Missing reset token");
    }
  }, [token]);

  return (
    <div className="w-full max-w-md space-y-6 text-foreground">
      <div className="flex justify-center">
        <Link to="/">
          <img src="/assets/images/retreevo-logo.svg" alt="" className="h-10 w-auto" />
        </Link>
      </div>

      <div className="rounded-xl border bg-card shadow-sm p-8 space-y-6">
        <div className="space-y-1 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">Set a new password</h1>
          <p className="text-sm text-muted-foreground">Choose something you'll remember.</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="newPassword">New password</Label>
            <Input
              id="newPassword"
              type="password"
              autoComplete="new-password"
              required
              {...register("newPassword", {
                required: true,
                validate: hookFormValidatePassword({ minChars: PASSWORD_MIN_LENGTH }),
              })}
            />
            {errors.newPassword ? (
              <p className="text-xs text-destructive">
                {(errors.newPassword.message as string) || PASSWORD_REQUIREMENTS_HINT}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">{PASSWORD_REQUIREMENTS_HINT}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="confirmPassword">Confirm password</Label>
            <Input
              id="confirmPassword"
              type="password"
              autoComplete="new-password"
              required
              {...register("confirmPassword", {
                required: true,
                validate: (value) => value === newPassword || "Passwords do not match",
              })}
            />
            {errors.confirmPassword && (
              <p className="text-xs text-destructive">
                {(errors.confirmPassword.message as string) ?? "Passwords do not match"}
              </p>
            )}
          </div>

          {serverError && <p className="text-sm text-destructive">{serverError}</p>}

          <Button
            type="submit"
            className="w-full"
            disabled={resetPasswordResult.isLoading || !token}
          >
            {resetPasswordResult.isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Update password
          </Button>
        </form>

        <div className="text-center text-sm text-muted-foreground">
          <Link to="/login" className="font-medium text-foreground hover:text-primary">
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
